// @flow

import React, {type Element, type Node} from 'react';
import {Dropdown, DropdownToggle, DropdownMenu, DropdownItem} from 'reactstrap';
import {LazyTooltip} from 'app/components/general/LazyTooltip';

import {type ReactRef} from 'app/general/types';

export type MenuItemType = {|
  text: string | Element<any>,
  icon: string,
  disabled?: boolean,
  key: string | number,
  callback: (any) => Promise<any> | void,
  condition: () => boolean,
  itemProps?: {|href?: string, target?: string, rel?: string|},
|};

export type MenuItemsType = Array<MenuItemType>;

export type actionsGetterType = (
  object: any,
  buttonRef: () => ReactRef<any>
) => MenuItemsType;

type Props = {|
  actionsGetter: actionsGetterType,
  object: any,
  tag?: string,
  forceMenu: boolean,
  preventItemClickEvents: boolean,
  className: string,
  buttonContent?: Node,
  alwaysUseSpace?: boolean,
  renderSingleButtonClassName?: string,
  onClick?: (SyntheticMouseEvent<any>) => any,
|};

type State = {|
  pending: boolean,
  open: boolean,
|};

class ThreeDotsDropdownMenu extends React.PureComponent<Props, State> {
  static defaultProps = {
    forceMenu: false,
    preventItemClickEvents: false,
    className: '',
    alwaysUseSpace: false,
  };

  state = {
    pending: false,
    open: false,
  };

  pendingIcon = 'fas fa-circle-notch fa-spin';
  shouldForceUpdate = false;
  buttonEl = React.createRef<HTMLDivElement>();

  componentDidMount() {
    if (this.shouldForceUpdate) {
      this.forceUpdate();
    }
  }

  toggle = () => this.setState((prevState) => ({open: !prevState.open}));

  getIcon(icon: string) {
    return this.state.pending ? this.pendingIcon : icon;
  }

  renderSingle = ({text, icon, disabled, callback}: MenuItemType) => (
    // render a single icon-only button if the length is 1
    <LazyTooltip placement="left" tooltip={text}>
      <button
        onClick={(ev) => this.captureCallbackPromise(callback, ev)}
        className={
          this.props.renderSingleButtonClassName ||
          'btn btn-link text-dark py-0 px-1'
        }
        disabled={disabled === true || this.state.pending}
      >
        <i className={`fa-fw ${this.getIcon(icon)}`} />
      </button>
    </LazyTooltip>
  );

  captureCallbackPromise = (cb: (...any) => any, ev: SyntheticEvent<any>) => {
    this.setState({pending: true});

    if (this.props.preventItemClickEvents) {
      ev.stopPropagation();
      ev.preventDefault();
    }

    const restorePending = (e) => {
      this.setState({pending: false});
      return e;
    };

    Promise.resolve(cb(this.props.object)).then(restorePending, restorePending);
  };

  buttonElGetter = () => {
    this.shouldForceUpdate = true;
    return this.buttonEl;
  };

  getButtonContent(actions: MenuItemsType) {
    if (actions.length === 0 && this.props.alwaysUseSpace === true) {
      return this.renderSingle({
        text: '',
        icon: '',
        disabled: true,
        callback: () => undefined,
        condition: () => true,
        key: 1,
      });
    } else if (actions.length === 1 && this.props.forceMenu === false) {
      return this.renderSingle(actions[0]);
    }
    if (this.props.buttonContent != null) {
      return this.props.buttonContent;
    }
    return <i className={`fa fa-fw ${this.getIcon('fa-ellipsis-v')}`} />;
  }

  getClassName() {
    return this.props.buttonContent == null
      ? `btn btn-link py-0 px-1 ${this.props.className}`
      : this.props.className;
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      object,
      actionsGetter,
      tag,
      forceMenu,
      preventItemClickEvents,
      buttonContent,
      className,
      alwaysUseSpace,
      ...other
    } = this.props;
    /* eslint-enable no-unused-vars */
    const {open, pending} = this.state;
    const actions = actionsGetter(object, this.buttonElGetter).filter((item) =>
      item.condition()
    );
    const content = this.getButtonContent(actions);
    const ContainerTag = tag != null ? tag : 'div';

    if (actions.length === 0) {
      return alwaysUseSpace === true ? content : null;
    } else if (actions.length === 1 && this.props.forceMenu === false) {
      return content;
    }

    return (
      <div ref={this.buttonEl}>
        {open ? (
          <Dropdown
            className="js-edit-dropdown"
            isOpen={open}
            toggle={this.toggle}
            tag={ContainerTag}
          >
            <DropdownToggle
              color=""
              className={this.getClassName()}
              {...other}
              disabled={pending}
            >
              {content}
            </DropdownToggle>
            <DropdownMenu right>
              {actions != null &&
                actions.map(
                  ({text, icon, disabled, callback, key, itemProps = {}}) => (
                    <DropdownItem
                      className="btn-sm btn-link"
                      onClick={(ev) =>
                        this.captureCallbackPromise(callback, ev)
                      }
                      disabled={disabled}
                      {...itemProps}
                      key={key}
                    >
                      <i className={`fa-fw ${icon}`} /> {text}
                    </DropdownItem>
                  )
                )}
            </DropdownMenu>
          </Dropdown>
        ) : (
          /* performance: don't render the menu if it`s not opened */
          <ContainerTag className="js-edit-dropdown">
            <button
              className={this.getClassName()}
              /* $FlowFixMe This comment suppresses an error
               * found when upgrading Flow to v0.114.0. To view
               * the error, delete this comment and run Flow. */
              {...other}
              disabled={pending}
              onClick={this.toggle}
            >
              {content}
            </button>
          </ContainerTag>
        )}
      </div>
    );
  }
}

export default ThreeDotsDropdownMenu;
